import React from 'react';
import { useSelector } from 'react-redux';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
// Components
import SelectDropdown from '../../components/SelectDropdown';
import Table from '../../components/Table';
import NoData from '../../components/NoData';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';
import { ToggleComponent } from '../../components/Toggle';
import ReportDownloadComponent from '../../components/DownloadReport/ReportDownloadComponent';
// Custom hooks
import useActualSpendData from './hooks/useActualSpendData';
// Constants and utilities
import {
  LOCAL_BRANCH_SPEND,
  ACTUAL_SPEND_YEAR_FILTER,
  getSettingValue,
  ENABLE_LOCAL_SPEND,
} from '../../constants/utils';
import selectors from '../../modules/selectors/datePickerSelectors';
// Styles
import { styles } from './styles/actualspendStyles';

// Main component
const ActualSpend = () => {
  // Redux selectors
  const { userData } = useSelector((state) => state.auth);
  const { transformedDataWithDate } = useSelector(selectors?.datePickerCombinedData);

  // Use the custom hook for all data and logic
  const {
    // Data and loading states
    weeklyTableHeaders,
    weeklyRowData,
    monthlyTableHeaders,
    monthlyRowData,
    errors,
    weeklyLoading,
    headersLoading,
    monthlyLoading,
    initialLoading, // Added initialLoading from the hook
    // Filter states
    selectedYear,
    spendToggleState,
    setSpendToggleState,
    showSpendToggle,
    resetDropdown,
    actualSpendView,
    // Filter data
    updatedChannelListForActualSpend,
    options,
    yearsList,
    // Channel selection state and logic (moved to the hook)
    modifiedRowData,
    modifiedMonthlyRowData,
    noDataForSelectedChannel,
    showNoChannelSelected,
    // Handler functions
    handleYearChange,
    getDataForStoreId,
    handleActualSpendView,
    changeSelectedFilters,
    // Download props functionality
    getDownloadProps,
  } = useActualSpendData();

  // Get download props directly from the hook
  const downloadProps = getDownloadProps(userData, transformedDataWithDate);

  // Define renderView as a pure function that doesn't modify state
  const renderCurrentView = () => {
    // Use initialLoading to show loader immediately on page load
    const isLoading = initialLoading || weeklyLoading || headersLoading || monthlyLoading;

    // If we're in initial loading state, show loader immediately
    if (isLoading) {
      return (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margin: 'auto',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      );
    }

    // Determine which rows to use based on view type and data availability
    const getRows = () => {
      if (actualSpendView === 'Weekly') {
        return modifiedRowData.length ? modifiedRowData : weeklyRowData;
      } else {
        return modifiedMonthlyRowData.length ? modifiedMonthlyRowData : monthlyRowData;
      }
    };
    const rows = getRows();
    const columns = actualSpendView === 'Weekly' ? weeklyTableHeaders : monthlyTableHeaders;

    // Handle no channel selected
    if ((showNoChannelSelected || noDataForSelectedChannel) && !isLoading) {
      return (
        <div style={styles.noDataContainer}>
          <NoData
            textValue={
              noDataForSelectedChannel
                ? 'No data available for selected channel'
                : 'No Channel selected'
            }
          />
        </div>
      );
    }

    // Handle errors
    if (errors.weekly_data_error || errors.headers_error || errors.monthly_data_error) {
      return <NoData textValue="Error Loading data" />;
    }

    // Handle data display
    if (rows?.length > 1 && columns?.length >= 3) {
      return (
        <div style={styles.tableContainer}>
          <Table
            boldHeaders={true}
            stickyColumns={['channel', 'audience', 'total']}
            data={rows}
            columns={columns}
            spendsToggle={spendToggleState}
          />
        </div>
      );
    }

    // No data case
    return (
      <div style={styles.noDataContainer}>
        <NoData textValue="No Data Available" />
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.titleContainer}>
        <div style={styles.titleWrapper}>
          <span>Actual Spend</span>
          {showSpendToggle && getSettingValue(userData?.data?.settings, ENABLE_LOCAL_SPEND) && (
            <ToggleComponent
              setState={setSpendToggleState}
              currentState={spendToggleState}
              disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
              toggleText={LOCAL_BRANCH_SPEND}
            />
          )}
        </div>
      </div>
      <div style={styles.filtersWrapper}>
        <div style={styles.filtersContainer}>
          <SelectDropdown
            options={options}
            label={'Select Branch'}
            handleFilterChange={getDataForStoreId}
            singleSelectDefaultValue={options[0]?.value}
            resetValues={resetDropdown}
            width={'200px'}
            marginExist={false}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          />
          <SelectDropdown
            options={updatedChannelListForActualSpend}
            label={'Select Channel'}
            isSelectAll={true}
            allOptionsSelectedText={'All Channels'}
            multiple={true}
            multiSelectDefaultValues={
              updatedChannelListForActualSpend?.map((channel) => channel?.value) || []
            }
            handleFilterChange={changeSelectedFilters}
            resetValues={resetDropdown}
            width={'200px'}
            marginExist={false}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          />
          {yearsList?.length > 0 &&
            getSettingValue(userData?.data?.settings, ACTUAL_SPEND_YEAR_FILTER) && (
              <SelectDropdown
                label={'Select Year'}
                singleSelectDefaultValue={yearsList[0]?.value}
                options={yearsList}
                handleFilterChange={handleYearChange}
                value={selectedYear}
                width="200px"
                disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
                marginExist={false}
              />
            )}
        </div>
        <ToggleButtonGroup
          value={actualSpendView}
          onChange={handleActualSpendView}
          exclusive
          aria-label="Toggle Spends And Opps Table View"
          sx={{ paddingRight: '0px', paddingTop: '10px' }}
        >
          <ToggleButton
            value={'Weekly'}
            style={styles.toggleButton(actualSpendView === 'Weekly')}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          >
            Weekly
          </ToggleButton>
          <ToggleButton
            value={'Monthly'}
            style={styles.toggleButton(actualSpendView === 'Monthly')}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          >
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>
        <ReportDownloadComponent {...downloadProps} />
      </div>
      <div>
        <h3 style={styles.heading}>{`${actualSpendView} Actual Spend`}</h3>
        {renderCurrentView()}
      </div>
    </div>
  );
};

export default ActualSpend;
